import { Field, HStack, Input, Switch, Text } from "components/ui";
import {
  CreateCustomerCompanyNotificationChannelRequest,
  CustomerCompanyNotificationChannel,
  CustomerCompanyNotificationProvider,
  CustomerCompanySlackSettingsSummary,
} from "services/customerCompanyAdminService";

/**
 * Credential fields for each notification provider a CustomerCompany can use,
 * plus the form state they share. IntegrationsTab owns the list, the dialog and
 * the save/test/delete actions; this file only knows how one provider's settings
 * are entered, validated and turned into a request body.
 *
 * Secrets are write-only: the API returns summaries without the password or bot
 * token, so a blank secret on an existing channel means "keep the stored one".
 */

/** One editor's fields — only the block matching the provider is rendered. */
export interface ChannelFormState {
  name: string;
  // Smtp
  host: string;
  port: string;
  enableSsl: boolean;
  username: string;
  password: string;
  fromEmail: string;
  displayName: string;
  // Telegram / Slack
  botToken: string;
  botUsername: string;
  // Slack
  signingSecret: string;
  // Teams
  appId: string;
  clientSecret: string;
  tenantId: string;
}

export const EMPTY_CHANNEL_FORM: ChannelFormState = {
  name: "",
  host: "",
  port: "587",
  enableSsl: true,
  username: "",
  password: "",
  fromEmail: "",
  displayName: "",
  botToken: "",
  botUsername: "",
  signingSecret: "",
  appId: "",
  clientSecret: "",
  tenantId: "",
};

/** Default name offered when adding a provider, before the admin renames it. */
export const defaultChannelName = (
  provider: CustomerCompanyNotificationProvider,
): string =>
  provider === "Telegram"
    ? "Telegram bot"
    : provider === "Slack"
      ? "Slack workspace"
      : provider === "Teams"
        ? "Assistant"
        : "SMTP";

/**
 * Prefills the editor from an existing channel. Secrets stay blank by design.
 * Takes only the fields it reads, so the tenant-wide channel page can reuse it.
 */
export const channelFormFrom = (
  channel: Pick<
    CustomerCompanyNotificationChannel,
    "name" | "smtpSettings" | "telegramSettings" | "teamsSettings"
  >,
): ChannelFormState => ({
  ...EMPTY_CHANNEL_FORM,
  name: channel.name,
  host: channel.smtpSettings?.host ?? "",
  port: String(channel.smtpSettings?.port ?? 587),
  enableSsl: channel.smtpSettings?.enableSsl ?? true,
  username: channel.smtpSettings?.username ?? "",
  fromEmail: channel.smtpSettings?.fromEmail ?? "",
  displayName: channel.smtpSettings?.displayName ?? "",
  botUsername: channel.telegramSettings?.botUsername ?? "",
  appId: channel.teamsSettings?.appId ?? "",
  tenantId: channel.teamsSettings?.tenantId ?? "",
});

type Translate = (key: string, options?: Record<string, unknown>) => string;

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Returns the first validation message, or null when the form can be saved.
 * `existing` relaxes the secret requirement — an existing channel already has
 * one stored. A new Slack channel may be saved without a token: its manifest
 * only exists once the channel does. `original` is the edited channel as it was
 * loaded: the stored SMTP password is only kept for the same server and account.
 */
export const validateChannelForm = (
  form: ChannelFormState,
  provider: CustomerCompanyNotificationProvider,
  existing: boolean,
  t: Translate,
  original?: ChannelFormState | null,
): string | null => {
  if (!form.name.trim()) {
    return t("app.customerCompanies.integrations.nameRequired", {
      defaultValue: "Channel name is required.",
    });
  }

  if (provider === "Smtp") {
    if (!form.host.trim()) {
      return t("app.customerCompanies.integrations.requiredFields", {
        defaultValue: "Channel name and SMTP host are required.",
      });
    }
    const port = Number(form.port);
    if (!Number.isInteger(port) || port <= 0 || port > 65535) {
      return t("app.customerCompanies.integrations.invalidPort", {
        defaultValue: "Port must be a number between 1 and 65535.",
      });
    }
    const serverChanged =
      !!original &&
      (form.host.trim().toLowerCase() !== original.host.trim().toLowerCase() ||
        port !== Number(original.port) ||
        form.username.trim() !== original.username.trim() ||
        (original.enableSsl && !form.enableSsl));
    if (serverChanged && original?.username.trim() && !form.password) {
      return t("app.customerCompanies.integrations.passwordReentry", {
        defaultValue:
          "Enter the password again: the host, port or username changed, or SSL was turned off.",
      });
    }
    return null;
  }

  if (provider === "Telegram" && !existing && !form.botToken.trim()) {
    return t("app.customerCompanies.integrations.botTokenRequired", {
      defaultValue: "A bot token is required.",
    });
  }

  if (provider === "Teams") {
    if (!GUID.test(form.appId.trim())) {
      return t("app.customerCompanies.integrations.teams.appIdInvalid", {
        defaultValue:
          "The Microsoft App ID must be a GUID, e.g. 3fa85f64-5717-4562-b3fc-2c963f66afa6.",
      });
    }
    if (!GUID.test(form.tenantId.trim())) {
      return t("app.customerCompanies.integrations.teams.tenantIdInvalid", {
        defaultValue:
          "The Directory (tenant) ID must be a GUID, e.g. 3fa85f64-5717-4562-b3fc-2c963f66afa6.",
      });
    }
    if (!existing && !form.clientSecret.trim()) {
      return t(
        "app.customerCompanies.integrations.teams.clientSecretRequired",
        {
          defaultValue: "A client secret is required.",
        },
      );
    }
  }
  return null;
};

/** The settings block for the edited provider, with blank secrets dropped. */
export const buildChannelSettings = (
  form: ChannelFormState,
  provider: CustomerCompanyNotificationProvider,
): Omit<
  CreateCustomerCompanyNotificationChannelRequest,
  "name" | "providerType"
> => {
  if (provider === "Telegram") {
    return {
      telegramSettings: {
        botToken: form.botToken.trim() || null,
        botUsername: form.botUsername.trim() || null,
      },
    };
  }
  if (provider === "Slack") {
    return {
      slackSettings: {
        botToken: form.botToken.trim() || null,
        signingSecret: form.signingSecret.trim() || null,
      },
    };
  }
  if (provider === "Teams") {
    return {
      teamsSettings: {
        appId: form.appId.trim(),
        clientSecret: form.clientSecret.trim() || null,
        tenantId: form.tenantId.trim(),
      },
    };
  }
  return {
    smtpSettings: {
      host: form.host.trim(),
      port: Number(form.port),
      enableSsl: form.enableSsl,
      username: form.username.trim() || null,
      password: form.password.trim() || null,
      fromEmail: form.fromEmail.trim() || null,
      displayName: form.displayName.trim() || null,
    },
  };
};

/**
 * A Slack channel's one-line state. It can exist before its token (created to
 * get the manifest), and without a signing secret its buttons stay off.
 */
export const slackChannelStatus = (
  channel: Pick<
    CustomerCompanyNotificationChannel,
    "slackSettings" | "inboundSetup"
  >,
  t: Translate,
): string => {
  if (!channel.slackSettings?.hasBotToken) {
    return t("app.customerCompanies.integrations.slack.noTokenYet", {
      defaultValue: "No token yet",
    });
  }
  const stored = t("app.customerCompanies.integrations.tokenStored", {
    defaultValue: "Token stored",
  });
  return channel.inboundSetup?.slackEventsUrl &&
    !channel.slackSettings.hasSigningSecret
    ? `${stored} · ${t(
        "app.customerCompanies.integrations.slack.noSigningSecret",
        {
          defaultValue: "no signing secret, buttons off",
        },
      )}`
    : stored;
};

export interface FieldsProps {
  form: ChannelFormState;
  onChange: (patch: Partial<ChannelFormState>) => void;
  /** True when editing an existing channel — secrets may then be left blank. */
  existing: boolean;
  /** What the edited Slack channel has stored; the other providers ignore it. */
  slackSettings?: CustomerCompanySlackSettingsSummary | null;
  t: Translate;
}

export const SmtpFields = ({ form, onChange, existing, t }: FieldsProps) => (
  <>
    <HStack gap={3} align="flex-end">
      <Field
        label={t("app.customerCompanies.integrations.fields.host", {
          defaultValue: "Host",
        })}
        required
        flex={2}
      >
        <Input
          value={form.host}
          onChange={(e) => onChange({ host: e.target.value })}
        />
      </Field>
      <Field
        label={t("app.customerCompanies.integrations.fields.port", {
          defaultValue: "Port",
        })}
        flex={1}
      >
        <Input
          value={form.port}
          inputMode="numeric"
          onChange={(e) => onChange({ port: e.target.value })}
        />
      </Field>
    </HStack>

    <HStack justify="space-between">
      <Text fontWeight="medium">
        {t("app.customerCompanies.integrations.fields.ssl", {
          defaultValue: "Use SSL/TLS",
        })}
      </Text>
      <Switch
        checked={form.enableSsl}
        onCheckedChange={(e: any) => onChange({ enableSsl: e.checked })}
      />
    </HStack>

    <Field
      label={t("app.customerCompanies.integrations.fields.username", {
        defaultValue: "Username",
      })}
    >
      <Input
        value={form.username}
        onChange={(e) => onChange({ username: e.target.value })}
        autoComplete="off"
      />
    </Field>

    <Field
      label={t("app.customerCompanies.integrations.fields.password", {
        defaultValue: "Password",
      })}
      helperText={
        existing
          ? t("app.customerCompanies.integrations.fields.passwordHelp", {
              defaultValue: "Leave blank to keep the stored password.",
            })
          : undefined
      }
    >
      <Input
        type="password"
        value={form.password}
        onChange={(e) => onChange({ password: e.target.value })}
        autoComplete="new-password"
      />
    </Field>

    <Field
      label={t("app.customerCompanies.integrations.fields.fromEmail", {
        defaultValue: "From address",
      })}
    >
      <Input
        value={form.fromEmail}
        onChange={(e) => onChange({ fromEmail: e.target.value })}
      />
    </Field>

    <Field
      label={t("app.customerCompanies.integrations.fields.displayName", {
        defaultValue: "From display name",
      })}
    >
      <Input
        value={form.displayName}
        onChange={(e) => onChange({ displayName: e.target.value })}
      />
    </Field>
  </>
);

export const TelegramFields = ({
  form,
  onChange,
  existing,
  t,
}: FieldsProps) => (
  <>
    <Field
      label={t("app.customerCompanies.integrations.telegram.botToken", {
        defaultValue: "Bot token",
      })}
      required={!existing}
      helperText={
        existing
          ? t("app.customerCompanies.integrations.secretHelp", {
              defaultValue: "Leave blank to keep the stored token.",
            })
          : t("app.customerCompanies.integrations.telegram.botTokenHelp", {
              defaultValue:
                "Create a bot with @BotFather in Telegram and paste the token it returns.",
            })
      }
    >
      <Input
        type="password"
        value={form.botToken}
        onChange={(e) => onChange({ botToken: e.target.value })}
        autoComplete="new-password"
        placeholder="123456789:AA…"
      />
    </Field>

    <Field
      label={t("app.customerCompanies.integrations.telegram.botUsername", {
        defaultValue: "Bot username",
      })}
      helperText={t(
        "app.customerCompanies.integrations.telegram.botUsernameHelp",
        { defaultValue: "Optional — read from the bot when left blank." },
      )}
    >
      <Input
        value={form.botUsername}
        onChange={(e) => onChange({ botUsername: e.target.value })}
        placeholder="acme_support_bot"
      />
    </Field>
  </>
);

export const SlackFields = ({
  form,
  onChange,
  existing,
  slackSettings,
  t,
}: FieldsProps) => (
  <>
    <Field
      label={t("app.customerCompanies.integrations.slack.botToken", {
        defaultValue: "Bot token",
      })}
      helperText={
        existing && slackSettings?.hasBotToken
          ? t("app.customerCompanies.integrations.secretHelp", {
              defaultValue: "Leave blank to keep the stored token.",
            })
          : t("app.customerCompanies.integrations.slack.botTokenHelp", {
              defaultValue:
                "Bot User OAuth Token (xoxb-…). No Slack app yet? Save without it — you then get a manifest that creates the app with every scope it needs.",
            })
      }
    >
      <Input
        type="password"
        value={form.botToken}
        onChange={(e) => onChange({ botToken: e.target.value })}
        autoComplete="new-password"
        placeholder="xoxb-…"
      />
    </Field>

    {existing && (
      <Field
        label={t("app.customerCompanies.integrations.slack.signingSecret", {
          defaultValue: "Signing secret",
        })}
        helperText={
          slackSettings?.hasSigningSecret
            ? t("app.customerCompanies.integrations.slack.signingSecretKeep", {
                defaultValue: "Leave blank to keep the stored secret.",
              })
            : t("app.customerCompanies.integrations.slack.signingSecretHelp", {
                defaultValue:
                  "From the app's Basic Information page. Needed for Approve/Deny buttons, replies and commands.",
              })
        }
      >
        <Input
          type="password"
          value={form.signingSecret}
          onChange={(e) => onChange({ signingSecret: e.target.value })}
          autoComplete="new-password"
        />
      </Field>
    )}
  </>
);
